<template>
    <div class="section">
        <Headbar>
            <template v-slot:left>
                <h1>{{ $t('countries.countries') }}</h1>
            </template>
            <template v-slot:right>
                <Button className="--primary --small" :onclick="toggleCreate" v-if="$store.getters.hasAnyPermission(['store countries'])">{{$t('countries.add_country')}}</Button>
            </template>
        </Headbar>

        <main>
            <vue-good-table
                mode="remote"
                styleClass="vgt-table vgt-custom"
                :columns="columns"
                :rows="countries"
                :isLoading.sync="is_loading_countries"
                :search-options="{
                    enabled: false,
                }"
                :pagination-options="{
                    enabled: true,
                    mode: 'records',
                    dropdownAllowAll: false,
                    perPage: 10,
                    perPageDropdownEnabled: true,
                    rowsPerPageLabel: $t('x_per_page', {x: $t('countries.countries')}),
                }"
                :sort-options="{
                  enabled: true,
                  multipleColumns: true,
                }"
                :totalRows="totalRecords"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
                @on-sort-change="onSortChange">
                <template slot="table-row" slot-scope="props">
                    <div v-if="props.column.field === 'after'" class="td-after">
                        <Button className="--secondary --outline --mini --big-text" v-if="$store.getters.hasPermission('update countries')"
                                :onclick="()=>toggleUpdate(props.row)">
                            <font-awesome-icon :icon="['fal', 'pencil']"/>
                        </Button>
                        <Button className="--secondary --outline --mini --big-text" v-if="$store.getters.hasPermission('destroy countries')"
                                :onclick="()=>toggleDelete(props.row)">
                            <font-awesome-icon :icon="['fal', 'trash']"/>
                        </Button>
                    </div>
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </vue-good-table>
        </main>
    </div>
</template>

<script>
import Headbar from "../../components/headbar/Headbar";
import Search from "../../components/Search";
import Button from "../../components/Button";
import Select from "../../components/form/Select";
import ConfirmModal from "../../components/modal/ConfirmModal";
import CreateModal from "./CountriesCreateModal";
import UpdateModal from "./CountriesUpdateModal";
import {EventBus} from "../../EventBus";

export default {
    name: "countries-index",
    components: {ConfirmModal, Select, Button, Search, Headbar},
    data: function () {
        const columns = [
            {
                label: this.$t('countries.code'),
                field: 'attributes.code',
                sortable: false,
            },
            {
                label: this.$t('countries.name'),
                field: 'attributes.name',
                sortable: false,
            },
            {
                label: this.$t('countries.groups'),
                field: row => row.relationships.countryGroups.data.length ? row.relationships.countryGroups.data.map(c => c.attributes.name).join(', ') : '',
                sortable: false,
            }
        ];

        if (this.$store.getters.hasAnyPermission(['update countries', 'destroy countries']))
            columns.push({
                label: this.$t('countries.actions'),
                field: 'after',
                tdClass: 'td-after',
                sortable: false
            });

        return {
            columns: columns,
            countries: [],
            is_loading_countries: false,
            totalRecords: null,
            serverParams: {
                sorting: [],
                per_page: 10
            },
            searchTerm: null,
            is_deleting: false,
            headbarExpanded: false
        }
    },
    methods: {
        toggleCreate() {
            this.$modal.show(
                CreateModal, {},
                {
                    name: 'countries-create-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    width: 450,
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.getCountries();
                    }
                }
            );
        },
        toggleUpdate(country) {
            this.$modal.show(
                UpdateModal,
                {
                    country_id: country.id,
                },
                {
                    name: 'countries-update-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    width: 450,
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.getCountries();
                    }
                }
            );
        },
        toggleDelete(country) {
            this.$modal.show(
                ConfirmModal, {
                    title: this.$t('countries.delete_country'),
                    message: this.$t('countries.prompt_delete'),
                    confirmText: this.$t('delete'),
                    cancelText: this.$t('cancel'),
                    confirmClass: '--primary',
                    cancelClass: '--secondary --outline'
                },
                {
                    name: 'confirm-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                },
                {
                'before-close': (e) => {
                    if (e.params === true) {
                        this.is_deleting = true;
                        this.$axios.delete(`countries/${country.id}`)
                            .then(({data}) => {
                                this.is_deleting = false;
                                this.getCountries();
                            })
                            .catch(e => {
                                this.is_deleting = false;

                                this.$notify({
                                    title: this.$t('error'),
                                    text: this.$larerror(e.response.data, this.$t('countries.error_delete')),
                                    type: 'error',
                                });
                            });
                        }
                    }
                }
            );
        },
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },
        onPageChange(params) {
            this.updateParams({page: params.currentPage});
            this.getCountries();
        },
        onPerPageChange(params) {
            this.updateParams({per_page: params.currentPerPage});
            this.getCountries();
        },
        onSortChange(params) {
            const sorts = [];

            if (!params || !params.length) {
                this.updateParams({sorting: []});
                return this.getCountries();
            }

            params.forEach(p => {
                if (!p.type || p.type === 'none')
                    return;

                let sort_by = null;
                let sort_order = p.type;
                if (p.field === 'attributes.name')
                    sort_by = 'name';
                else if (p.field === 'attributes.email')
                    sort_by = 'email';
                else if (p.field === 'role')
                    sort_by = 'role';
                else
                    sort_by = p.field.split('.')[1];

                sorts.push({sort_order, sort_by})
            });

            this.updateParams({sorting: sorts});
            this.getCountries();
        },
        removeParam(param) {
            this.$delete(this.serverParams, param);
        },
        search(searchTerm) {
            this.searchTerm = searchTerm;

            if (searchTerm && searchTerm.length)
                this.updateParams({term: searchTerm});
            else this.removeParam('term');

            this.getCountries();
        },
        getCountries() {
            this.is_loading_countries = true;

            const encodedSorting = this.serverParams.sorting.map(f => btoa(JSON.stringify(f)));

            this.$axios.get('countries', {params: {...this.serverParams, sorting: encodedSorting}})
                .then(({data}) => {
                    this.countries = data.data;
                    this.totalRecords = data.meta.total;
                    this.is_loading_countries = false;
                })
                .catch(e => {
                    this.is_loading_countries = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('countries.error_retrieve')),
                        type: 'error',
                    });
                }
            );
        }
    },
    mounted() {
        EventBus.$on('get-countries', this.getCountries);
        this.getCountries();
    },
    beforeDestroy() {
        EventBus.$off('get-countries');
    }
}
</script>

<style lang="scss" scoped>
.page-container {
    @apply flex flex-col;

    @screen lg {
        @apply flex-row;
    }

    main, .headbar-container {
        &:first-of-type {
            @screen lg {
                @apply pr-4;
            }

            @screen xl {
                @apply pr-6;
            }
        }

        &:last-of-type {
            @screen lg {
                @apply pl-4;
            }

            @screen xl {
                @apply pl-6;
            }
        }
    }

    .headbar-expanded-container {
        @apply mt-4 flex flex-col gap-y-4 items-center;

        @screen md {
            @apply hidden;
        }
    }

    .select-dropdown {
        @apply min-w-40;
    }

    .btn-search-mobile, .btn-filter-mobile {
        @apply block text-primary w-6;

        @screen md {
            @apply hidden;
        }

        &:active, &:focus {
            @apply outline-none;
        }
    }

    .search-desktop, .filter-desktop {
        @apply hidden;

        @screen md {
            @apply block;
        }
    }

    .search-container {
        @apply mr-0;
    }

    .filter-mobile {
        @apply max-w-xs;
    }

    .td-after {
        @apply flex flex-row;

        & > * {
            @apply mr-3;

            &:last-child {
                @apply mr-0;
            }
        }
    }
}
</style>
